import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const defaultConfig = () => ({
  contact: { city: '', state: '', county: '', country: '', updateTimezone: false },
  company: { city: '', state: '', county: '', country: '', timezone: '' },
  experimentalGlobal: { enabled: false, contactCountryProperty: 'country', companyCountryProperty: 'country' }
});

const outputLabels = {
  city: 'City / Town',
  state: 'State / Province / Region',
  county: 'County / District',
  country: 'Country',
  timezone: 'Timezone'
};

function PortalConfiguration() {
  const { portalId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [contactProperties, setContactProperties] = useState([]);
  const [companyProperties, setCompanyProperties] = useState([]);
  const [contactCountryProperties, setContactCountryProperties] = useState([]);
  const [companyCountryProperties, setCompanyCountryProperties] = useState([]);
  const [config, setConfig] = useState(defaultConfig);
  
  // Check if this is a new connection
  const isNewConnection = new URLSearchParams(location.search).get('new_connection') === 'true';

  useEffect(() => {
    fetchProperties();
    fetchExistingConfig();
  }, [portalId]);

  const fetchProperties = async () => {
    try {
      const getProperties = (object, source = false) => axios.get(
        `${process.env.REACT_APP_API_URL}/api/hubspot/${object}-properties/${portalId}${source ? '?purpose=country-source' : ''}`,
        { headers: { Authorization: `Bearer ${user.token}` } }
      );
      const [contactResponse, companyResponse, contactSources, companySources] = await Promise.all([
        getProperties('contact'), getProperties('company'), getProperties('contact', true), getProperties('company', true)
      ]);
      setContactProperties(contactResponse.data);
      setCompanyProperties(companyResponse.data);
      setContactCountryProperties(contactSources.data);
      setCompanyCountryProperties(companySources.data);
    } catch (error) {
      console.error('Error fetching properties:', error.response ? error.response.data : error.message);
      setError(`Failed to fetch properties: ${error.response ? error.response.data.error : error.message}`);
    }
  };

  const fetchExistingConfig = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/hubspot/portal-config/${portalId}`, {
        headers: {
          Authorization: `Bearer ${user.token}`
        }
      });
      const defaults = defaultConfig();
      setConfig({
        contact: { ...defaults.contact, ...response.data.contact },
        company: { ...defaults.company, ...response.data.company },
        experimentalGlobal: { ...defaults.experimentalGlobal, ...response.data.experimentalGlobal }
      });
    } catch (error) {
      console.error('Failed to fetch existing config:', error);
      setError('Failed to load configuration. Reload this page before saving.');
      setConfig(null);
    }
    setLoading(false);
  };

  const handleConfigChange = (section, field, value, type = 'value') => {
    const newValue = type === 'checkbox' ? !config[section][field] : value;
    setConfig(prevConfig => ({
      ...prevConfig,
      [section]: {
        ...prevConfig[section],
        [field]: newValue
      }
    }));
  };

  const saveConfig = async () => {
    setError('');
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/hubspot/save-portal-config/${portalId}`, config, {
        headers: {
          Authorization: `Bearer ${user.token}`
        }
      });
      navigate('/dashboard');
    } catch (error) {
      console.error('Error saving configuration:', error.response ? error.response.data : error.message);
      setError(`Failed to save configuration: ${error.response ? error.response.data.error : 'Network error'}`);
    }
  };

  if (loading) return <div>Loading...</div>;
  if (!config || !config.contact) return <div role="alert">{error || 'Loading configuration...'}</div>;

  return (
    <div className="card">
      <h2>Portal Configuration</h2>
      
      {isNewConnection && (
        <div style={{ 
          backgroundColor: 'rgba(76, 175, 80, 0.1)', 
          border: '1px solid #4CAF50',
          borderRadius: '8px', 
          padding: '15px 20px', 
          marginBottom: '20px',
          color: '#2e7d32'
        }}>
          <strong>🎉 HubSpot Connected Successfully!</strong>
          <p style={{ margin: '10px 0 0 0', fontSize: '0.95rem' }}>
            We've pre-configured your portal with HubSpot's default City, State/Region, Country, and Timezone fields. 
            Review the settings below and customize them if needed, then click <strong>Save Configuration</strong> to finish setup.
          </p>
        </div>
      )}
      
      {error && <div style={{ color: 'red', marginBottom: '15px' }}>Error: {error}</div>}
      <fieldset style={{ margin: '20px 0', padding: '16px' }}>
        <legend>International lookup (experimental)</legend>
        <label htmlFor="experimental-global-enabled" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <input
            id="experimental-global-enabled"
            type="checkbox"
            checked={config.experimentalGlobal.enabled}
            onChange={e => handleConfigChange('experimentalGlobal', 'enabled', e.target.checked)}
          />
          International lookup — 2 credits per successful lookup
        </label>
        <p>
          Sends the record's postal code and country directly to Google Geocoding. Coverage is experimental;
          city/town, state/province/region, county/district, country, and timezone availability varies by country.
          Location names are returned in English where available.
        </p>
        <p id="international-country-required">
          <strong>Country is required for every international lookup, including US and Canada.</strong>{' '}
          With this feature enabled, US ZIP codes and Canadian postal codes will not be looked up unless
          the record's selected country source contains a recognized country. Missing or unrecognized
          countries result in no updates and no credit charge.
        </p>
        <p>
          This setting applies only to future postal-code changes and does not backfill existing records.
          Failed lookups use no credits.
        </p>
        <p>
          Choose the destination for each field below. State / Province / Region uses the country's
          first administrative level; County / District uses the second. Missing or ambiguous values
          leave existing fields unchanged.
        </p>
        {config.experimentalGlobal.enabled && [
          ['contactCountryProperty', 'Contact country source', contactCountryProperties],
          ['companyCountryProperty', 'Company country source', companyCountryProperties]
        ].map(([field, label, properties]) => (
          <div key={field}>
            <label htmlFor={field}>{label}:</label>
            <select
              id={field}
              value={config.experimentalGlobal[field]}
              onChange={e => handleConfigChange('experimentalGlobal', field, e.target.value)}
            >
              <option value="country">Country (standard country property)</option>
              {properties.filter(prop => prop.name !== 'country').map(prop => (
                <option key={prop.name} value={prop.name}>{prop.label} ({prop.name})</option>
              ))}
            </select>
          </div>
        ))}
      </fieldset>
      <p>Output destinations below show writable text properties without validation rules. Country sources also support dropdown and read-only properties.</p>
      <h3>Contact Settings</h3>
      {['city', 'state', 'county', 'country'].map(field => (
        <div key={`contact-${field}`}>
          <label htmlFor={`contact-${field}`}>{outputLabels[field]}:</label>
          <select
            id={`contact-${field}`}
            value={config.contact[field] || ''}
            onChange={(e) => handleConfigChange('contact', field, e.target.value)}
          >
            <option value="">Do Not Update with Location Data</option>
            {contactProperties.map(prop => (
              <option key={prop.name} value={prop.name}>{prop.label}</option>
            ))}
          </select>
        </div>
      ))}
      {/* Timezone Checkbox for Contacts - Improved Styling */}
      <div key="contact-timezone" style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}> 
        <label htmlFor="contact-updateTimezone" style={{ marginRight: '8px' }}>Update Timezone:</label>
        <input 
            type="checkbox" 
            id="contact-updateTimezone"
            checked={config.contact.updateTimezone || false} // Ensure value is controlled
            onChange={() => handleConfigChange('contact', 'updateTimezone', null, 'checkbox')}
            style={{ margin: '0 8px 0 0' }} // Adjust checkbox margin
        />
        <span style={{ fontSize: '0.9em', color: '#555' }}>(Updates the default `hs_timezone` property)</span>
      </div>

      <h3>Company Settings</h3>
      {['city', 'state', 'county', 'country', 'timezone'].map(field => (
        <div key={`company-${field}`}>
          <label htmlFor={`company-${field}`}>{outputLabels[field]}:</label>
          <select
            id={`company-${field}`}
            value={config.company?.[field] || ''}
            onChange={(e) => handleConfigChange('company', field, e.target.value)}
          >
            <option value="">Do Not Update with Location Data</option>
            {companyProperties.map(prop => (
              <option key={prop.name} value={prop.name}>{prop.label}</option>
            ))}
          </select>
        </div>
      ))}
      <p>Company timezone is optional. International lookups use Google Time Zone API with the coordinates returned by Google Geocoding.</p>
      <br />
      <button onClick={saveConfig}>Save Configuration</button>
    </div>
  );
}

export default PortalConfiguration;
